// ─────────────────────────────────────────────────────────────
// DRIFTSHOT CLIENT - Main Game Client
// ─────────────────────────────────────────────────────────────
// This file orchestrates the game client, connecting all systems.
// Core state is managed by GameState (public/core/GameState.js)
// Game loop is managed by GameLoop (public/core/GameLoop.js)
// Network is managed by SocketManager (public/network/SocketManager.js)
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// CORE INSTANCES
// ─────────────────────────────────────────────────────────────
import { abilityRegistry } from '../shared/AbilityRegistry.js';
import { CollisionUtils } from '../shared/CollisionUtils.js';
import { registerStandardItemKinds } from '../shared/ItemKinds.js';
import { consumableRegistry } from '../shared/ConsumableRegistry.js';
import { getDefaultWeapon, weaponRegistry, WeaponFireMode } from '../shared/WeaponRegistry.js';
import { Protocol } from '../shared/protocol.js';
import { resolveSound } from '../shared/resolveSound.js';
import { spreadOffsets, shotSeqFor, canFireAtSeq } from '../shared/ShotModel.js';
import { LocalPlayer } from './LocalPlayer.js';
import { RemotePlayer } from './RemotePlayer.js';
import { Auth } from './auth.js';
import { InvisiblePresenceCue } from './audio/InvisiblePresenceCue.js';
import { PlayerSoundManager } from './audio/PlayerSoundManager.js';
import { SpatialAudioManager } from './audio/SpatialAudioManager.js';
import { SPATIAL_AUDIO_CONFIG } from './audio/spatial-audio-config.js';
import { configRegistry } from './core/ConfigRegistry.js';
import { GameLoop } from './core/GameLoop.js';
import { GameState } from './core/GameState.js';
import { Loadout } from './core/Loadout.js';
import { createLoadoutStore } from './core/LoadoutStore.js';
import { resolveBackpack } from './core/backpacks.js';
import { stepProjectiles } from './core/ProjectileSim.js';
import { TiledMapLoader } from './core/TiledMapLoader.js';
import { InputManager } from './input/InputManager.js';
import { ClientLogger } from './logger/ClientLogger.js';
import { SocketManager } from './network/SocketManager.js';
import { AbilityEvents } from './network/events/AbilityEvents.js';
import { CombatEvents } from './network/events/CombatEvents.js';
import { GameEvents } from './network/events/GameEvents.js';
import { LootContainerEvents } from './network/events/LootContainerEvents.js';
import { ItemEvents } from './network/events/ItemEvents.js';
import { MeleeEvents } from './network/events/MeleeEvents.js';
import { PickupEvents } from './network/events/PickupEvents.js';
import { RoomEvents } from './network/events/RoomEvents.js';
import { WeaponEvents } from './network/events/WeaponEvents.js';
import { CosmeticEvents } from './network/events/CosmeticEvents.js';
import { RankedEvents } from './network/events/RankedEvents.js';
import { ProgressionEvents } from './network/events/ProgressionEvents.js';
import { FeedbackEvents } from './network/events/FeedbackEvents.js';
import { FlagStore } from './feedback/FlagStore.js';
import { FeedbackComposer } from './ui/FeedbackComposer.js';
import { AnimationManager } from './rendering/animation/AnimationManager.js';
import { PixiRenderer } from './rendering/pixi/PixiRenderer.js';
import { PixiWorldRenderer } from './rendering/pixi/PixiWorldRenderer.js';
import { createLockerPreview } from './ui/lockerPreview.js';
import { UIManager } from './ui/UIManager.js';
import { DebugStartGameButton } from './ui/DebugStartGameButton.js';
import { NotificationSystem } from './ui/NotificationSystem.js';

const flagStore = FlagStore.create();

async function patchFlagNote(id, note) {
  const res = await fetch(`/api/feedback/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ note })
  });
  if (!res.ok) throw new Error('patch failed');
  flagStore.markNoted(id);
}

async function pendingFlagsForRecap() {
  const local = flagStore.pending().map((f) => ({ id: f.id, summary: f.summary }));
  try {
    const roomId = flagStore.roomId || gameState.currentRoomId;
    const res = await fetch(`/api/feedback/me${roomId ? `?roomId=${encodeURIComponent(roomId)}` : ''}`, {
      credentials: 'same-origin'
    });
    if (!res.ok) return local;
    const { items } = await res.json();
    const fromServer = (items || []).filter((i) => !i.note).map((i) => ({ id: i.id, summary: i.summary }));
    const seen = new Set(fromServer.map((i) => i.id));
    for (const f of local) {
      if (!seen.has(f.id)) fromServer.push(f);
    }
    return fromServer;
  } catch {
    return local;
  }
}

function closeComposerInput() {
  InputManager.capturingText = false;
}

function openComposer(id) {
  InputManager.capturingText = true;
  FeedbackComposer.open({
    id,
    onSubmit: async (flagId, note) => {
      closeComposerInput();
      if (!note) return;
      try {
        await patchFlagNote(flagId, note);
      } catch {
        NotificationSystem.error("Couldn't save flag");
        openComposer(flagId);
      }
    },
    onLater: () => { closeComposerInput(); }
  });
}

function sendFlag() {
  if (FeedbackComposer.isOpen()) return false;
  if (!Auth.isLoggedIn()) {
    NotificationSystem.warning('Sign in to flag');
    return false;
  }
  if (gameState.currentRoomId) flagStore.setRoom(gameState.currentRoomId);
  socketManager.send('flagMoment', {});
  return true;
}

const clientLoggerFactory = ClientLogger;
const nativeConsole = typeof window !== 'undefined' && window.console ? window.console : null;
const fallbackLogger = {
  error: (...args) => nativeConsole && nativeConsole.error && nativeConsole.error(...args),
  warn: (...args) => nativeConsole && nativeConsole.warn && nativeConsole.warn(...args),
  info: (...args) => nativeConsole && nativeConsole.log && nativeConsole.log(...args),
  debug: (...args) => nativeConsole && nativeConsole.log && nativeConsole.log(...args),
  trace: (...args) => nativeConsole && nativeConsole.log && nativeConsole.log(...args)
};

function createClientLogger(category) {
  if (clientLoggerFactory && typeof clientLoggerFactory.getLogger === 'function') {
    return clientLoggerFactory.getLogger(category);
  }
  return fallbackLogger;
}

const clientLogger = createClientLogger('client');
const networkLogger = createClientLogger('network');
const lootLogger = createClientLogger('loot');
const audioLogger = createClientLogger('audio');
const devLogger = createClientLogger('dev');

const gameState = new GameState();

// ─────────────────────────────────────────────────────────────
// RENDERER CONFIGURATION
// ─────────────────────────────────────────────────────────────
// Renderer - using PixiJS WebGL renderer only
let pixiRendererInitialized = false;

// Toggle debug collision visualization with F10
document.addEventListener('keydown', (e) => {
  if (e.key === 'F10') {
    if (typeof PixiWorldRenderer !== 'undefined' && PixiWorldRenderer.toggleDebugCollisions) {
      PixiWorldRenderer.toggleDebugCollisions();
      const isOn = PixiWorldRenderer.debugCollisions;
      showNotification(`Debug collisions: ${isOn ? 'ON' : 'OFF'}`, 'info', 2000);
    }
  }
});

// Toggle the floating debug info panel with \
document.addEventListener('keydown', (e) => {
  if (e.key === '\\') {
    debugPanelVisible = !debugPanelVisible;
    DebugStartGameButton.setDebugPanelVisible(debugPanelVisible);
    showNotification(`Debug panel: ${debugPanelVisible ? 'ON' : 'OFF'}`, 'info', 2000);
  }
});

// Socket manager - handles connection lifecycle and event registration
const socketManager = new SocketManager(gameState);
const socket = socketManager.getSocket(); // Backward compatibility
// Exposed for console debugging only — no client code reads window.socket any
// more. Client→server messages must go through socketManager.send() so they are
// checked against shared/protocol.js.
// Player sound manager - handles footsteps and other player sounds
const playerSoundManager = new PlayerSoundManager();

// Spatial audio manager - handles distance-based volume and stereo panning
const spatialAudio = new SpatialAudioManager(SPATIAL_AUDIO_CONFIG.default);

// Assigned once the renderer is up (see initialize()); RoomEvents calls it on map rotation
let reloadMap = null;
window.__spatialAudio = spatialAudio; // console debugging only

let invisiblePresenceCue = null;

// Create/resume the AudioContext on first user interaction (autoplay policy).
// Must init here: resume() is a no-op until the context exists, and footsteps
// now play through Web Audio rather than HTML Audio.
document.addEventListener('click', () => {
  spatialAudio.init().then(() => spatialAudio.resume());
}, { once: true });

// Handle a protocol version mismatch - the server refused our messages because
// this client is running against a different shared/protocol.js than it is.
socketManager.setOnProtocolMismatch((info) => {
  networkLogger.error('Protocol version mismatch - client must reload', info);
  UIManager.setReadyButtonState('outdated');
  showNotification('Game updated — please refresh to keep playing.', 'error', 15000);
});

// Handle reconnections - reset to lobby when reconnecting after server restart
// A transport drop mid-match is recoverable: re-send the same join request so
// the server can rebind us to our frozen entity. Only fall back to the lobby if
// no rejoin (or rejoinFailed) comes back in time. A drop during the pre-match
// warm-up goes straight to the lobby: the server has already dropped us.
socketManager.setOnReconnect(() => {
  if (gameState.shouldAttemptRejoin()) {
    networkLogger.warn('Reconnected mid-match - attempting rejoin');
    if (typeof showNotification === 'function') showNotification('Reconnecting…', 'warning');
    gameState.rejoinPending = true;
    socketManager.send('setPlayerName', gameState.lastJoinPayload);
    clearTimeout(gameState.rejoinTimer);
    gameState.rejoinTimer = setTimeout(() => {
      if (gameState.rejoinPending) {
        networkLogger.warn('No rejoin from server within 3s - returning to lobby');
        resetToLobbyAfterReconnect();
      }
    }, 3000);
    return;
  }
  resetToLobbyAfterReconnect();
});

/** Tear the match down and put the player back on the splash screen. */
function resetToLobbyAfterReconnect() {
  gameState.rejoinPending = false;
  clearTimeout(gameState.rejoinTimer);
  networkLogger.warn('Reconnected to server - resetting to lobby');

  teardownToLobby();

  // Show notification
  showNotification('Reconnected to server. Please ready up again.', 'info', 4000);
}

/**
 * The local half of going back to the lobby, shared by every path that does it
 * (game-end Return to Lobby, a reconnect that can't resume, a confirmed Unready
 * during warm-up): stop pings and sounds, reset game state and this file's
 * local accessors, clear the renderer and input, and restore the lobby view.
 * Never touches the socket — callers decide whether and when to disconnect.
 */
function teardownToLobby() {
  // Stop ping tracking if it was running (the next Ready starts a fresh one)
  socketManager.stopPingTracking();

  // Stop all player and spatial sounds
  playerSoundManager.stopAll();
  if (spatialAudio && typeof spatialAudio.stopAll === 'function') {
    spatialAudio.stopAll();
  }

  // Reset game state
  gameState.reset();
  DebugStartGameButton.setRoomStatus(null);

  // Sync local accessors with reset state
  // Connection state
  isInGame = gameState.isInGame;
  localId = gameState.localId;
  currentRoomId = gameState.currentRoomId;
  roomState = gameState.roomState;

  // Entity collections - these are new array/Map references after reset
  projectiles = gameState.projectiles;
  predictedProjectiles = gameState.predictedProjectiles;
  serverProjectileState = gameState.serverProjectileState;

  // Visual effects - these are new arrays after reset
  eliminationFeed = gameState.eliminationFeed;
  floatingDamageNumbers = gameState.floatingDamageNumbers;

  // Ability state - new object after reset
  abilityState = gameState.abilityState;

  // Loot containers - new Map after reset
  lootContainers = gameState.lootContainers;

  // Reset renderers to clear cached sprites
  if (typeof PixiRenderer !== 'undefined' && PixiRenderer.reset) {
    PixiRenderer.reset();
  }

  // Clear input
  if (typeof InputManager !== 'undefined' && InputManager.clearAllInput) {
    InputManager.clearAllInput();
  }

  // Reset and show splash screen
  UIManager.resetSplashScreen();
  UIManager.showSplashScreen();
}

// Convenience constants from GameState
const DEBUG_MODE = GameState.DEBUG_MODE;
const INTERPOLATION_DELAY = GameState.INTERPOLATION_DELAY;
const RECONCILE_THRESHOLD = GameState.RECONCILE_THRESHOLD;
const RECONCILE_SMOOTH_FACTOR = GameState.RECONCILE_SMOOTH_FACTOR;
const DAMAGE_NUMBER_DURATION = GameState.DAMAGE_NUMBER_DURATION;
const DAMAGE_NUMBER_FLOAT_SPEED = GameState.DAMAGE_NUMBER_FLOAT_SPEED;
const DAMAGE_NUMBER_FADE_START = GameState.DAMAGE_NUMBER_FADE_START;
const DAMAGE_NUMBER_HORZ_MIN = GameState.DAMAGE_NUMBER_HORZ_MIN;
const DAMAGE_NUMBER_HORZ_MAX = GameState.DAMAGE_NUMBER_HORZ_MAX;

// ─────────────────────────────────────────────────────────────
// TRANSITIONAL ACCESSORS
// These provide backward compatibility while we migrate to gameState
// ─────────────────────────────────────────────────────────────
// Players map accessor
const players = gameState.players;
const keys = gameState.keys;
const inputState = gameState.inputState;

// Projectile accessors - these need getter/setter for array reassignment
let projectiles = gameState.projectiles;
let predictedProjectiles = gameState.predictedProjectiles;
let serverProjectileState = gameState.serverProjectileState;
let predictedProjectileIdCounter = 0;

// Map accessors
let mapWidth = gameState.mapWidth;
let mapHeight = gameState.mapHeight;
let mapConfig = gameState.mapConfig;

// Connection state accessors
let localId = gameState.localId;
let isInGame = gameState.isInGame;
let playerName = gameState.playerName;
let currentRoomId = gameState.currentRoomId;
let roomState = gameState.roomState;

// Lobby mode data (fetched from server)
let lobbyGameModes = [];
let lobbyDefaultModeId = null;

// World objects accessor
const worldObjects = gameState.worldObjects;
let lootContainers = gameState.lootContainers;

function ensureLootInteractionState() {
  if (!gameState.lootContainerInteraction) {
    gameState.lootContainerInteraction = {
      activeContainerId: null,
      progress: 0,
      holdDurationMs: 0,
      isHolding: false,
      localStartTime: 0,
      lastServerProgressMs: 0
    };
  }
  return gameState.lootContainerInteraction;
}

// Effects accessors
let eliminationFeed = gameState.eliminationFeed;
let eliminationNotification = gameState.eliminationNotification;
let deathNotification = gameState.deathNotification;
let floatingDamageNumbers = gameState.floatingDamageNumbers;
const damageNumberToggle = gameState.damageNumberToggle;

// Ability state accessor
let abilityState = gameState.abilityState;

// Debug state accessors
let mouseWorldX = gameState.mouseWorldX;
let mouseWorldY = gameState.mouseWorldY;
let debugPanelVisible = false; // off by default; toggled by \, see keydown listener above

// Performance tracking
let currentFps = 0;
let frameCount = 0;
let lastFpsUpdate = performance.now();
let currentPing = 0;
let lastPingTime = 0;

// Projectile tracking
let clientLastShotTime = gameState.clientLastShotTime;
let pendingGamepadReload = false;

// ─────────────────────────────────────────────────────────────
// CONFIG LOADING (from JSON files)
// ─────────────────────────────────────────────────────────────
async function loadConfigs() {
  // Point the item façade at the per-kind registries before anything is
  // fetched: the adapters read their registry live, so ordering only matters
  // for lookups, not for registration (5D/D2).
  registerStandardItemKinds();

  try {
    const [ui, cosmetics, abilities, game, consumables] = await Promise.all([
      fetch('/config/ui.json').then(r => r.json()),
      fetch('/config/cosmetics.json').then(r => r.json()),
      fetch('/config/abilities.json').then(r => r.json()),
      fetch('/config/game.json').then(r => r.json()),
      fetch('/config/consumables.json').then(r => r.json())
    ]);
    gameState.uiConfig = ui;
    gameState.cosmeticsConfig = cosmetics;
    gameState.abilitiesConfig = abilities;
    gameState.gameConfig = game;

    // Consumables must be in the registry before any snapshot arrives, or a
    // slot holding one cannot be rebuilt on this side.
    if (consumables?.consumables) {
      consumableRegistry.registerBulk(consumables.consumables);
      for (const consumable of consumables.consumables) loadItemIcon(consumable.icon);
    }

    // Register abilities in the client-side registry
    if (abilities?.abilities && typeof abilityRegistry !== 'undefined') {
      abilities.abilities.forEach(abilityConfig => {
        abilityRegistry.register(abilityConfig);
        
        // Preload ability icon
        if (abilityConfig.icon) {
          const iconImage = new Image();
          iconImage.src = abilityConfig.icon;
          iconImage.onload = () => {
            clientLogger.debug(`[Client] Loaded ability icon: ${abilityConfig.id}`);
          };
          abilityIconCache.set(abilityConfig.id, iconImage);
        }
      });
    }
    return true;
  } catch (err) {
    clientLogger.warn('Failed to load configs', err);
  }
}

function initializeInvisiblePresenceCue() {
  if (typeof InvisiblePresenceCue === 'undefined') {
    return;
  }
  if (!invisiblePresenceCue) {
    invisiblePresenceCue = new InvisiblePresenceCue({ gameState, spatialAudio });
  }
  const soundConfig = gameState.gameConfig?.player?.sounds?.invisibility_whisper || null;
  invisiblePresenceCue.setSoundConfig(soundConfig);
}

async function loadLobbyGameModes() {
  try {
    const response = await fetch('/api/game-modes');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const payload = await response.json();
    lobbyGameModes = (Array.isArray(payload.modes) ? payload.modes : [])
      .filter((mode) => mode && mode.enabled !== false);
    lobbyDefaultModeId = payload.defaultMode || null;
  } catch (err) {
    clientLogger.warn('Failed to load lobby game modes', err);
  }
}

// ─────────────────────────────────────────────────────────────
// WEAPON SPRITE SYSTEM
// ─────────────────────────────────────────────────────────────
// Sprite cache for weapons and projectiles
const spriteCache = {};

// Track last shot time per player for animation state (from gameState)
const playerLastShotTime = gameState.playerLastShotTime;

// Debug flag for visualizing muzzle offset (from gameState)
let debugMuzzleOffset = gameState.debugMuzzleOffset;

/**
 * Preload weapon and projectile sprites from WeaponRegistry
 */
function preloadWeaponSprites() {
  const weapons = weaponRegistry.getAll();

  weapons.forEach(weapon => {
    // Load weapon sprite (fallback/default)
    if (weapon.weaponSprite && !spriteCache[weapon.weaponSprite]) {
      const img = new Image();
      img.src = weapon.weaponSprite;
      img.onerror = () => clientLogger.warn(`✗ Failed to load weapon sprite: ${weapon.weaponSprite}`);
      spriteCache[weapon.weaponSprite] = img;
    }

    // Load weapon idle sprite
    if (weapon.weaponSpriteIdle && !spriteCache[weapon.weaponSpriteIdle]) {
      const img = new Image();
      img.src = weapon.weaponSpriteIdle;
      img.onerror = () => clientLogger.warn(`✗ Failed to load weapon idle sprite: ${weapon.weaponSpriteIdle}`);
      spriteCache[weapon.weaponSpriteIdle] = img;
    }

    // Load weapon firing sprite
    if (weapon.weaponSpriteFiring && !spriteCache[weapon.weaponSpriteFiring]) {
      const img = new Image();
      img.src = weapon.weaponSpriteFiring;
      img.onerror = () => clientLogger.warn(`✗ Failed to load weapon firing sprite: ${weapon.weaponSpriteFiring}`);
      spriteCache[weapon.weaponSpriteFiring] = img;
    }

    // Load projectile sprite
    if (weapon.projectileSprite && !spriteCache[weapon.projectileSprite]) {
      const img = new Image();
      img.src = weapon.projectileSprite;
      img.onerror = () => clientLogger.warn(`✗ Failed to load projectile sprite: ${weapon.projectileSprite}`);
      spriteCache[weapon.projectileSprite] = img;
    }

    // Load weapon icon for inventory display
    if (weapon.icon && !weaponIconCache.has(weapon.id)) {
      weaponIconCache.set(weapon.id, loadItemIcon(weapon.icon));
    }
  });
}

// ─────────────────────────────────────────────────────────────
// INPUT MANAGEMENT
// ─────────────────────────────────────────────────────────────
// Input is now handled by modular input handlers in /input/:
// - GamepadInput.js: Gamepad/controller support
// - KeyboardInput.js: Keyboard input handling
// - MouseInput.js: Mouse input handling
// - InputManager.js: Unified input coordinator
//
// InputManager is initialized in the initialize() function at the bottom.
// Backward compatibility: gamepadManager reference for existing code
let gamepadManager = null; // Set after InputManager.init()

// ─────────────────────────────────────────────────────────────
// ABILITY SYSTEM STATE (defined in GameState, accessors at top of file)
// ─────────────────────────────────────────────────────────────

// Ability icon cache
const abilityIconCache = new Map();
const defaultAbilityIcon = new Image();
defaultAbilityIcon.src = 'assets/art/ui/genericAbilityIcon.png';

// Weapon icon cache, keyed by weapon id (kept for the sprite preloader)
const weaponIconCache = new Map();

/**
 * Inventory icons, keyed by icon path.
 *
 * The HUD asks for an icon by its path, so one cache covers every kind a slot
 * can hold. Entries are filled by the weapon preloader and on first request,
 * which is what lets a consumable show its icon without any preloading step of
 * its own.
 * @type {Map<string, HTMLImageElement>}
 */
const itemIconCache = new Map();

/**
 * Load an inventory icon and remember it under its path.
 * @param {string} iconPath
 * @returns {HTMLImageElement|null}
 */
function loadItemIcon(iconPath) {
  if (!iconPath) return null;
  if (itemIconCache.has(iconPath)) return itemIconCache.get(iconPath);

  const img = new Image();
  img.src = iconPath;
  img.onerror = () => clientLogger.warn(`✗ Failed to load item icon: ${iconPath}`);
  itemIconCache.set(iconPath, img);
  return img;
}

const PICKUP_INTERACT_RADIUS = 100;
let deathSoundAudio = null;
let eliminationSoundAudio = null;

// World object image cache
// Images are loaded dynamically when world objects are received from server
const worldObjectImageCache = new Map();
const NON_LOOPING_ANIMATION_STATES = new Set(['opening', 'opened', 'destroyed', 'hidden', 'cooldown']);

/**
 * Preload the visual assets required for a world object (static + animations)
 * @param {Object} obj
 */
function preloadWorldObjectVisual(obj) {
  if (!obj) return;

  if (obj.graphic && !worldObjectImageCache.has(obj.id)) {
    const img = new Image();
    img.src = obj.graphic;
    img.onerror = () => clientLogger.warn(`Failed to load world object image: ${obj.graphic}`);
    worldObjectImageCache.set(obj.id, img);
  }

  if (obj.animation && typeof AnimationManager !== 'undefined') {
    for (const path of Object.values(obj.animation)) {
      if (path) {
        AnimationManager.ensure(path);
      }
    }
  }
}

/**
 * Compute render data for a world object (static image or animation frame)
 * @param {Object} obj
 * @returns {{type: 'static', image: HTMLImageElement}|{type: 'animation', image: HTMLImageElement, frame: Object, cacheKey: string}|null}
 */
function getWorldObjectRenderData(obj) {
  if (!obj) return null;

  if (obj.animation && obj.animationState && typeof AnimationManager !== 'undefined') {
    const animationPath = obj.animation[obj.animationState];
    if (animationPath) {
      const loop = !NON_LOOPING_ANIMATION_STATES.has(obj.animationState);
      const startTime = obj.animationStateStartTime || Date.now();
      const elapsedMs = Math.max(0, Date.now() - startTime);
      const durationOverride = loop ? null : (obj.animationDurations?.[obj.animationState] || obj.animationDurationMs);

      const frame = AnimationManager.getFrame(animationPath, elapsedMs, {
        loop,
        durationMs: durationOverride
      });

      if (frame) {
        return {
          type: 'animation',
          image: frame.image,
          frame: frame.frame,
          cacheKey: frame.cacheKey || `${animationPath}|${frame.frameIndex}`
        };
      }
    }
  }

  const img = worldObjectImageCache.get(obj.id);
  if (img && img.complete && img.naturalWidth > 0) {
    return { type: 'static', image: img };
  }

  return null;
}

// Teleport trail accessor (defined in GameState)
const teleportTrails = gameState.teleportTrails;

// Helper functions now delegate to GameState
function isLocalPlayerDead() {
  return gameState.isLocalPlayerDead();
}

function canLocalPlayerShoot() {
  return gameState.canLocalPlayerShoot();
}

function canLocalPlayerReload() {
  return gameState.canLocalPlayerReload();
}

// ─────────────────────────────────────────────────────────────
// NOTIFICATION SYSTEM
// ─────────────────────────────────────────────────────────────
// Notifications are now handled by UIManager.notifications (NotificationSystem.js)
// Backward compatibility function - delegates to UIManager
function showNotification(message, type = 'info', duration = 3000) {
  UIManager.showNotification(message, type, duration);
}

// ─────────────────────────────────────────────────────────────
// RENDERING
// ─────────────────────────────────────────────────────────────
// Using PixiJS WebGL renderer only (initialized later in initializeRenderer())

// Skin management system
const skinImages = new Map();
const skinLoadPromises = [];

// Lobby loadout (6A): what the player will wear on spawn. Seeded by loadoutStore
// from /api/locker (account) or localStorage (guest); edited by the Locker.
let loadout = null;
let loadoutStore = null;

// Preload all available skins
function loadSkin(skinId, skinPath) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      skinImages.set(skinId, img);
      resolve(skinId);
    };
    img.onerror = () => {
      clientLogger.warn(`Failed to load skin: ${skinId} ${skinPath}, falling back to default`);
      resolve(skinId); // Resolve anyway to not block
    };
    img.src = skinPath;
  });
}

// Load skins from cosmetics config (called after config is loaded)
function loadAllSkins() {
  if (!gameState.cosmeticsConfig || !gameState.cosmeticsConfig.playerSkins) return;

  for (const skin of gameState.cosmeticsConfig.playerSkins) {
    skinLoadPromises.push(loadSkin(skin.id, skin.sprite));
  }
}

// Backpack art, keyed by catalog id. Entries with no sprite (the 'none'
// backpack) have nothing to load.
const backpackImages = new Map();

function loadAllBackpacks() {
  for (const backpack of gameState.cosmeticsConfig?.backpacks || []) {
    if (!backpack.sprite) continue;
    skinLoadPromises.push(new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { backpackImages.set(backpack.id, img); resolve(backpack.id); };
      img.onerror = () => {
        clientLogger.warn(`Failed to load backpack: ${backpack.id} ${backpack.sprite}`);
        resolve(backpack.id);
      };
      img.src = backpack.sprite;
    }));
  }
}

// The image for a backpack id (null/unknown → the default backpack), or null
// when the player wears no backpack or its art failed to load.
function getBackpackImage(backpackId) {
  const entry = resolveBackpack(backpackId, gameState.cosmeticsConfig);
  return entry ? backpackImages.get(entry.id) || null : null;
}

// Get teleport trail config from abilities config
function getTeleportTrailConfig() {
  if (!gameState.abilitiesConfig || !gameState.abilitiesConfig.abilities) {
    // Fallback defaults
    return { duration: 400, ghostCount: 5, startOpacity: 0.6, endOpacity: 0.1, color: '#00ffff' };
  }

  const teleportAbility = gameState.abilitiesConfig.abilities.find(a => a.id === 'teleport');
  if (teleportAbility && teleportAbility.effects && teleportAbility.effects.trail) {
    return teleportAbility.effects.trail;
  }

  // Fallback defaults
  return { duration: 400, ghostCount: 5, startOpacity: 0.6, endOpacity: 0.1, color: '#00ffff' };
}

// Helper function to get skin image (with fallback to default)
function getSkinImage(skinId) {
  return skinImages.get(skinId) || skinImages.get('default');
}

// Weapon system - get current weapon (for now, default laser rifle)
let currentWeapon = getDefaultWeapon();

// Sound effect cache for weapons
const weaponSoundCache = new Map();

/**
 * Get or load weapon sound
 * @param {Weapon} weapon - The weapon
 * @param {string} event - Sound event (e.g., 'fire', 'reload')
 * @returns {Audio|null}
 */
function getWeaponSound(weapon, event) {
  if (!weapon) return null;

  const resolved = typeof weapon.getSoundPlayback === 'function'
    ? weapon.getSoundPlayback(event)
    : resolveSound(weapon.sounds?.[event]);
  if (!resolved) return null;

  // Check cache
  const cacheKey = resolved.path;
  let audio;
  if (weaponSoundCache.has(cacheKey)) {
    audio = weaponSoundCache.get(cacheKey);
  } else {
    audio = new Audio(resolved.path);
    weaponSoundCache.set(cacheKey, audio);
  }
  audio.volume = resolved.volume;
  return audio;
}

// ─────────────────────────────────────────────────────────────
// RENDERING
// ─────────────────────────────────────────────────────────────
// Rendering handled by PixiJS WebGL renderer in /rendering/pixi/

function render(currentTime) {
  // Calculate render time for entity interpolation
  // Remote entities are rendered interpolationDelay() ms behind the estimated server clock
  // (adaptive: 2 broadcast intervals + observed jitter, capped — see NetStats)
  const renderTime = gameState.serverNow() - gameState.interpolationDelay();

  // Update FPS counter
  frameCount++;
  if (currentTime - lastFpsUpdate >= 1000) {
    currentFps = frameCount;
    frameCount = 0;
    lastFpsUpdate = currentTime;
  }

  const localPlayer = players.get(localId);
  // While spectating (after an early elimination), the camera follows the
  // spectate target instead of our own eliminated player.
  const cameraSubject = (gameState.spectateTargetId && players.get(gameState.spectateTargetId)) || localPlayer;
  const localRenderState = cameraSubject ? cameraSubject.getRenderState(renderTime) : { x: 0, y: 0 };

  // Build render context
  const renderContext = {
    currentTime,
    renderTime,
    localRenderState,
    gameState,
    callbacks: {
      // World renderer callbacks
      getWorldObjectImage: (obj) => worldObjectImageCache.get(obj.id),
      getWorldObjectRenderData: (obj) => getWorldObjectRenderData(obj),

      // Entity renderer callbacks
      getSkinImage,
      getBackpackImage,
      getWeaponSprite: (path) => spriteCache[path],
      getProjectileSprite: (path) => spriteCache[path],
      getPlayerLastShotTime: (playerId) => playerLastShotTime.get(playerId),
      debugMuzzleOffset,
      INTERPOLATION_DELAY: gameState.interpolationDelay(),

      // Effects renderer callbacks
      getTeleportTrailConfig,

      // HUD renderer callbacks
      getAbilityIcon: (abilityId) => abilityIconCache.get(abilityId),
      // The HUD asks by icon path, not by item, so one path-keyed cache serves
      // every kind an inventory slot can hold — weapons and consumables today.
      getWeaponIcon: (iconPath) => {
        if (!iconPath) return null;
        return itemIconCache.get(iconPath) || loadItemIcon(iconPath);
      },
      getAbility: (abilityId) => typeof abilityRegistry !== 'undefined' ? abilityRegistry.get(abilityId) : null,
      defaultAbilityIcon,
      uiConfig: gameState.uiConfig,
      gameModeConfig: gameState.gameModeConfig,
      DEBUG_MODE: DEBUG_MODE && debugPanelVisible,
      mouseWorldX,
      mouseWorldY,

      // Performance metrics
      fps: currentFps,
      ping: currentPing,
      netStats: gameState.netStats.summary(),
      rendererType: 'WebGL'
    }
  };

  // Render with PixiJS
  if (pixiRendererInitialized) {
    PixiRenderer.render(renderContext);
  }
}

// ─────────────────────────────────────────────────────────────
// ELIMINATION NOTIFICATION (Center screen)
// ─────────────────────────────────────────────────────────────

/**
 * Show elimination notification in center of screen
 * Only shows for the local player when they get a kill, or (`kind` 'decoy')
 * when they shoot an enemy's decoy: same slot and timing, different words and
 * colour (see hud/eliminationBanner.js).
 * @param {string} victimId
 * @param {string} [victimName]
 * @param {'elimination'|'decoy'} [kind]
 */
function showEliminationNotification(victimId, victimName, kind = 'elimination') {
  const now = performance.now();
  eliminationNotification = {
    kind,
    victim: victimId,
    victimName: victimName || victimId,
    timestamp: now,
    expiresAt: now + gameState.uiConfig.eliminationNotification.duration
  };
  gameState.eliminationNotification = eliminationNotification;
}

/**
 * Update elimination notification state
 * Called every frame
 */
function updateEliminationNotification() {
  if (!eliminationNotification) return;

  const now = performance.now();
  if (now >= eliminationNotification.expiresAt) {
    eliminationNotification = null;
    gameState.eliminationNotification = null;
  }
}

function showDeathNotification(killerName) {
  const now = performance.now();
  const allowRespawn = gameState.gameModeConfig?.allowRespawn;
  if (!allowRespawn) {
    return;
  }
  const duration = gameState.uiConfig?.deathNotification?.duration || 2000;
  deathNotification = {
    killerName: killerName || 'Unknown',
    timestamp: now,
    expiresAt: now + duration
  };
  gameState.deathNotification = deathNotification;
}

function updateDeathNotification() {
  if (!deathNotification) return;
  const now = performance.now();
  if (now >= deathNotification.expiresAt) {
    deathNotification = null;
    gameState.deathNotification = null;
  }
}

function playDeathSound() {
  const resolved = resolveSound(gameState.gameConfig?.player?.sounds?.death);
  if (!resolved) {
    return;
  }
  try {
    if (!deathSoundAudio || deathSoundAudio.src.endsWith(resolved.path) === false) {
      deathSoundAudio = new Audio(resolved.path);
    }
    deathSoundAudio.pause();
    deathSoundAudio.currentTime = 0;
    deathSoundAudio.volume = resolved.volume;
    const soundConfig = gameState.gameConfig?.player?.sounds?.death;
    if (soundConfig?.playbackRate) {
      deathSoundAudio.playbackRate = soundConfig.playbackRate;
    } else {
      deathSoundAudio.playbackRate = 1;
    }
    deathSoundAudio.play().catch(() => {});
  } catch (err) {
    audioLogger.warn('Failed to play death sound', err);
  }
}

function playEliminationSound() {
  const resolved = resolveSound(gameState.gameConfig?.player?.sounds?.eliminate_player);
  if (!resolved) {
    return;
  }
  try {
    if (!eliminationSoundAudio || eliminationSoundAudio.src.endsWith(resolved.path) === false) {
      eliminationSoundAudio = new Audio(resolved.path);
    }
    eliminationSoundAudio.pause();
    eliminationSoundAudio.currentTime = 0;
    eliminationSoundAudio.volume = resolved.volume;
    const soundConfig = gameState.gameConfig?.player?.sounds?.eliminate_player;
    if (soundConfig?.playbackRate) {
      eliminationSoundAudio.playbackRate = soundConfig.playbackRate;
    } else {
      eliminationSoundAudio.playbackRate = 1;
    }
    eliminationSoundAudio.play().catch(() => {});
  } catch (err) {
    audioLogger.warn('Failed to play elimination sound', err);
  }
}

// ─────────────────────────────────────────────────────────────
// ELIMINATION FEED (Top-left corner)
// ─────────────────────────────────────────────────────────────

/**
 * Add a new elimination message to the feed
 * Handles queue management, expiration tracking, and size limits
 */
function addEliminationToFeed(killerId, victimId, killerName, victimName) {
  const now = performance.now();
  const newMessage = {
    killer: killerId,
    killerName: killerName || killerId,
    victim: victimId,
    victimName: victimName || victimId,
    timestamp: now,
    expiresAt: null // Will be set when it becomes the top message
  };

  // Add to feed (newest at the end)
  eliminationFeed.push(newMessage);

  // Set expiration time for the first message if this is the only message
  if (eliminationFeed.length === 1) {
    eliminationFeed[0].expiresAt = now + gameState.uiConfig.eliminationFeed.messageDuration;
  }

  // Enforce maximum feed size by removing oldest messages
  while (eliminationFeed.length > gameState.uiConfig.eliminationFeed.maxMessages) {
    eliminationFeed.shift();
    // Reset expiration for new top message
    if (eliminationFeed.length > 0) {
      eliminationFeed[0].expiresAt = now + gameState.uiConfig.eliminationFeed.messageDuration;
    }
  }
}

/**
 * Update elimination feed state - expires old messages and manages queue
 * Called every frame
 */
function updateEliminationFeed() {
  const now = performance.now();

  // Only expire the top (oldest) message if it has expired
  if (eliminationFeed.length > 0) {
    const topMessage = eliminationFeed[0];
    if (topMessage.expiresAt && now >= topMessage.expiresAt) {
      eliminationFeed.shift(); // Remove the top message

      // Set expiration time for the new top message
      if (eliminationFeed.length > 0) {
        eliminationFeed[0].expiresAt = now + gameState.uiConfig.eliminationFeed.messageDuration;
      }
    }
  }
}

/**
 * Update floating damage numbers - remove expired ones
 * Called every frame
 */
function updateFloatingDamageNumbers() {
  const now = performance.now();

  // Remove expired damage numbers
  for (let i = floatingDamageNumbers.length - 1; i >= 0; i--) {
    const dmg = floatingDamageNumbers[i];
    const age = now - dmg.createdAt;

    if (age >= DAMAGE_NUMBER_DURATION) {
      floatingDamageNumbers.splice(i, 1);
    }
  }
}

/**
 * Attempt to activate an ability by category
 * Uses client-side prediction for immediate feedback
 */
const lastAbilityActivation = { utility: 0, movement: 0 };
const ABILITY_ACTIVATION_DEBOUNCE = 100; // ms - prevent double activation

function activateAbility(category) {
  const equipped = abilityState.equipped[category];
  if (!equipped) {
    return;
  }

  const now = performance.now();

  // Debounce to prevent double-activation from rapid key presses or key repeat
  if (now - lastAbilityActivation[category] < ABILITY_ACTIVATION_DEBOUNCE) {
    return;
  }
  lastAbilityActivation[category] = now;

  // Always send to server - let server decide if it's a cancel or activation
  // (cancellable abilities like rewind will be handled server-side)
  console.log(`[Client] activateAbility: ${equipped.abilityId}, category: ${category}, isActive:`, abilityState.activeEffects[equipped.abilityId]);

  // Client-side cooldown check for immediate feedback
  const abilityCooldownEnd = abilityState.cooldowns[equipped.abilityId] || 0;
  const globalCooldowns = abilityState.globalCooldowns || {};
  const globalCooldownEnd = globalCooldowns[category] || 0;

  if (now < abilityCooldownEnd || now < globalCooldownEnd) {
    return;
  }

  // Send activation request to server
  socketManager.send('activateAbility', { category });

  // Client-side prediction: 
  // - Instant abilities (duration=0): start cooldown and global cooldown immediately
  // - Timed abilities (duration>0): defer cooldown to deactivation (server will tell us)
  const ability = typeof abilityRegistry !== 'undefined'
    ? abilityRegistry.get(equipped.abilityId)
    : null;

  if (ability) {
    const duration = ability.getDurationAtStack
      ? ability.getDurationAtStack(equipped.stacks)
      : ability.duration;
    
    // Only predict cooldown for instant abilities
    if (duration === 0) {
      const cooldown = ability.getCooldownAtStack
        ? ability.getCooldownAtStack(equipped.stacks)
        : ability.cooldown;
      abilityState.cooldowns[equipped.abilityId] = now + cooldown;
      const predictedEnd = now + abilityState.globalCooldownDuration;
      abilityState.globalCooldowns = abilityState.globalCooldowns || { utility: 0, movement: 0 };
      abilityState.globalCooldowns.utility = predictedEnd;
      abilityState.globalCooldowns.movement = predictedEnd;
    }
  }
}

// ─────────────────────────────────────────────────────────────
// INPUT HANDLING
// ─────────────────────────────────────────────────────────────
// Input gathering uses the InputManager to combine all input sources.
// Event handlers below are kept for game-specific logic (shooting, abilities).

function emitAimUpdate(angle) {
    const now = performance.now();
    if (now - inputState.lastAimSend > 33) {
        inputState.lastAimSend = now;
        socketManager.send('aim', {
            seq: inputState.aimSeq++,
            angle
        });
    }
}

function gatherCurrentInput() {
    if (FeedbackComposer.isOpen() || InputManager.capturingText) {
        return {
            up: false, down: false, left: false, right: false,
            drift: false, reload: false,
            mouseX: inputState.mouse.x, mouseY: inputState.mouse.y,
            angle: inputState.angle
        };
    }
    const gamepadReload = consumePendingGamepadReload();
    // Use InputManager to gather combined input from all sources
    // (keyboard, mouse, gamepad)
    if (gamepadManager) {
        // Integrate gamepad input with keyboard/mouse
        const leftStick = gamepadManager.getLeftStick();
        const rightStick = gamepadManager.getRightStick();

        // Movement: Combine keyboard + gamepad left stick
        const up = keys.w || keys.ArrowUp || leftStick.y < -0.3;
        const down = keys.s || keys.ArrowDown || leftStick.y > 0.3;
        const left = keys.a || keys.ArrowLeft || leftStick.x < -0.3;
        const right = keys.d || keys.ArrowRight || leftStick.x > 0.3;

        // Aiming: Use right stick if active, otherwise use mouse
        let angle = inputState.angle;
        const rightStickMagnitude = Math.sqrt(rightStick.x * rightStick.x + rightStick.y * rightStick.y);
        if (rightStickMagnitude > 0.3) {
            angle = Math.atan2(rightStick.y, rightStick.x);
            inputState.angle = angle;
            inputState.lastAimSource = 'gamepad';
            inputState.lastGamepadAngle = angle;
            emitAimUpdate(angle);
        } else if (inputState.lastAimSource === 'gamepad') {
            angle = inputState.lastGamepadAngle;
            inputState.angle = angle;
        }

        const reloadAllowed = canLocalPlayerReload() === true;
        return {
            up,
            down,
            left,
            right,
            drift: Boolean(keys[' ']) || gamepadManager.getTriggerValue('LT') > 0.1, // hold to slide (space / LT)
            reload: reloadAllowed && (keys.r || gamepadReload),
            mouseX: inputState.mouse.x,
            mouseY: inputState.mouse.y,
            angle
        };
    }

    // Fallback if gamepadManager not initialized (shouldn't happen)
    const reloadAllowed = canLocalPlayerReload() === true;
    return {
        up: keys.w || keys.ArrowUp,
        down: keys.s || keys.ArrowDown,
        left: keys.a || keys.ArrowLeft,
        right: keys.d || keys.ArrowRight,
        drift: Boolean(keys[' ']),
        reload: reloadAllowed && (keys.r || gamepadReload),
        mouseX: inputState.mouse.x,
        mouseY: inputState.mouse.y,
        angle: inputState.angle
    };
}

//input event listeners
document.addEventListener('keydown', (e) => {
  if (!isInGame) return; // Ignore input when not in game

  const key = e.key.toLowerCase();

  // Space is the drift key: never let it scroll the page or re-press a focused lobby button.
  if (key === ' ') e.preventDefault();

  // Check for manual pickup (e key)
  if (key === 'e' && !isLocalPlayerDead()) {
    const started = tryStartLootContainerInteraction();
    if (!started) {
      socketManager.send('manualPickup');
    }
    return; // Don't set keys.e to true for holds
  }

  if (key === 'r' && !isLocalPlayerDead()) {
    const reloadCheck = canLocalPlayerReload();
    if (reloadCheck !== true) {
      if (reloadCheck?.name) {
        showNotification(`Cannot reload while ${reloadCheck.name} is active!`, 'warning');
      }
      return;
    }
  }

  keys[key] = true;

  // Check for ability activation (Q, E)
  const abilityKeybinds = gameState.uiConfig?.controls?.abilityKeybinds || {};
  if (abilityKeybinds[key] && !isLocalPlayerDead()) {
    activateAbility(abilityKeybinds[key]);
  }
});
document.addEventListener('keyup', (e) => {
  const key = e.key.toLowerCase();
  if (key === ' ') e.preventDefault(); // buttons activate on space keyup
  if (key === 'e') {
    cancelActiveContainerInteraction('key_up');
  }
  keys[key] = false;
});

// Slot-switching (mouse wheel via InputManager's onInventorySwitch, gamepad
// LB/RB) all funnel through here — a single source of truth so no two input
// methods can race each other sending conflicting switchInventorySlot
// messages for the same scroll/press. Skips empty slots (Player.switchSlot).
function switchInventorySlot(direction) {
  const localPlayer = players.get(localId);
  if (localPlayer && typeof localPlayer.switchSlot === 'function') {
    localPlayer.switchSlot(direction);
    socketManager.send('switchInventorySlot', { slotIndex: localPlayer.activeSlotIndex });
  }
}

// Reset all keys when window loses focus or tab becomes hidden
function clearAllKeys() {
  for (let key in keys) {
    keys[key] = false;
  }
}

// Reset input state when tab becomes visible again to prevent desync
function handleTabVisible() {
  const player = players.get(localId);
  if (player && player.inputHistory) {
    // Clear input history to prevent stale inputs causing desync
    player.inputHistory = [];
  }
  // Reset game loop timing to prevent catch-up spam
  gameLoop.resetTiming();
}

window.addEventListener('blur', clearAllKeys);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    clearAllKeys();
  } else {
    // Tab became visible again
    handleTabVisible();
  }
});

// Mouse button state handling
// Shooting logic is handled by InputManager (onShoot callback) and handleMouseShooting() for full-auto
document.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    // Note: InputManager handles the actual shooting via onShoot callback
    // This handler is kept for any legacy code that checks inputState.mouse.down directly
});

document.addEventListener('mouseup', (e) => {
    if (e.button === 0) {
        inputState.mouse.down = false;
    }
});

document.addEventListener('mousemove', (e) => {
    // Mouse position and angle are now handled by InputManager/MouseInput
    // This handler just sends aim updates to the server and calculates debug world coords

    if (!isInGame) return; // Ignore input when not in game
    if (gameState.inventoryOpen) return; // Don't update aim when inventory is open

    // Debug mode: Calculate world coordinates
    if (DEBUG_MODE) {
        const canvas = PixiRenderer?.app?.canvas || document.querySelector('canvas');
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            // Use screen-space coordinates (PixiJS autoDensity handles DPR)
            const mx = Math.round(e.clientX - rect.left);
            const my = Math.round(e.clientY - rect.top);
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;

            const localPlayer = players.get(localId);
            if (localPlayer) {
                // Convert screen coordinates to world coordinates
                mouseWorldX = Math.round(mx - centerX + localPlayer.x);
                mouseWorldY = Math.round(my - centerY + localPlayer.y);
            }
        }
    }

    // Send aim updates to server (angle is managed by InputManager)
    emitAimUpdate(inputState.angle);
});

//input sending
function sendInputPacket(seq, input) {
    socketManager.send('input', { seq, ...input });
}

// ─────────────────────────────────────────────────────────────
// SOCKET EVENT HANDLERS
// ─────────────────────────────────────────────────────────────
// Socket events are now handled by modular event handlers:
// - RoomEvents.js: roomStatus, readyConfirmed, unreadyConfirmed, gameStarting, countdownCancelled, gameStarted
// - GameEvents.js: spawn, state, projectileSpawned, zoneState, zoneDamage
// - CombatEvents.js: damageTaken, damageDealt, playerEliminated, playerDied, gameEnd
// - AbilityEvents.js: abilityStateSync, abilityActivated, abilityDeactivated, abilityEffectEnded, etc.
// - WeaponEvents.js: weaponReloadStarted, weaponEmpty
// - PickupEvents.js: pickupCollected, pickupSpawned, pickupRemoved, pickupFailed
// - LootContainerEvents.js: lootContainerSpawned, lootContainerOpened, etc.
//
// Event modules are registered in registerNetworkEvents() at the bottom of this file.
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// SOUND HELPERS
// ─────────────────────────────────────────────────────────────
function playOtherPlayerShootSound(shotX, shotY, weaponId = null) {
    // Use the weapon ID if provided, otherwise fall back to current weapon
    const weapon = weaponId ? weaponRegistry.get(weaponId) : currentWeapon;
    if (!weapon) return;

    const resolved = typeof weapon.getSoundPlayback === 'function'
      ? weapon.getSoundPlayback('fire')
      : resolveSound(weapon.sounds?.fire);
    if (!resolved) return;

    // Play using spatial audio manager with weapon-specific configuration
    spatialAudio.playAt(resolved.path, shotX, shotY, {
        config: SPATIAL_AUDIO_CONFIG.weapons,
        volume: resolved.volume
    });
}


// ─────────────────────────────────────────────────────────────
// PREDICTION HELPERS
// ─────────────────────────────────────────────────────────────
function predictAndSpawnLocalBullet(seq = null) {
    const player = players.get(localId);
    if (!player || !player.isLocal) return;

    // Use the equipped weapon from the Player class
    const weapon = player.equippedWeapon;
    const weaponState = player.weaponState;
    if (!weapon || !weaponState) return;

    const now = performance.now();

    // Local validation — use client-side timing (not server state which has latency)
    if (weaponState.reloading || weaponState.currentAmmo <= 0) return;

    // Apply ability multipliers to fire rate (e.g., SuperSpeed)
    const fireRateMultiplier = player.activeAbilityEffects?.fireRateMultiplier || 1.0;
    const stacks = player.inventory[player.activeSlotIndex]?.stack || 1;
    const effectiveFireRate = (weapon.getFireRateAtStack?.(stacks) ?? weapon.fireRate) / fireRateMultiplier;

    // Use client-side shot tracking for fire rate (server state lags behind due to latency)
    if (!passesFireRate(player, seq, effectiveFireRate)) return 'rate';
    if (seq !== null) player.lastShotSeq = seq;

    // Update client-side shot time
    gameState.clientLastShotTime = now;
    clientLastShotTime = now;

    // Track shot time for weapon animation state (idle vs firing)
    playerLastShotTime.set(gameState.localId, now);

    // Use LocalPlayer's current predicted position + angle with weapon's muzzle offset
    const muzzleOffset = weapon.muzzleOffset || { x: 30, y: 0 };
    const cos = Math.cos(player.angle);
    const sin = Math.sin(player.angle);
    const spawnX = player.x + (muzzleOffset.x * cos - muzzleOffset.y * sin);
    const spawnY = player.y + (muzzleOffset.x * sin + muzzleOffset.y * cos);

    // Prevent spawning predicted projectile if muzzle is obstructed by world geometry
    // (ray from player center to muzzle spawn)
    // CollisionUtils is imported at the top of this module; the old
    // `if (window.CollisionUtils)` guard silently disabled this check once
    // Phase 3C stopped putting it on window.
    const block = CollisionUtils.getFirstCollisionPoint(player.x, player.y, spawnX, spawnY, worldObjects);
    if (block) {
      // Muzzle is inside/behind a wall — don't spawn local predicted projectile
      return;
    }

    // The server uses this same input seq for its pellet spread.
    const offsets = spreadOffsets(weapon, seq);
    for (let i = 0; i < offsets.length; i++) {
      // Calculate velocity with spread
      const projectileAngle = player.angle + offsets[i];
      const vx = Math.cos(projectileAngle) * weapon.projectileSpeed;
      const vy = Math.sin(projectileAngle) * weapon.projectileSpeed;

      // Precompute termination point - calculate max travel distance based on lifetime
      const maxTravelTime = weapon.projectileLifetime / 1000; // Convert to seconds
      const maxEndX = spawnX + vx * maxTravelTime;
      const maxEndY = spawnY + vy * maxTravelTime;

      // Find first collision along the projectile's path
      const collision = CollisionUtils.getFirstCollisionPoint(spawnX, spawnY, maxEndX, maxEndY, worldObjects,
        { shooterId: localId, shooterTeam: players.get(localId)?.team ?? null }); // own firewalls do not stop own tracers

      // Calculate termination time (when projectile should stop rendering)
      let terminationTime = weapon.projectileLifetime; // Default to max lifetime
      if (collision) {
        // t is 0-1 along the path from spawn to max distance
        terminationTime = collision.t * maxTravelTime * 1000; // Convert back to ms
      }

      // Spawn predicted projectile with precomputed termination
      predictedProjectiles.push({
          id: ++predictedProjectileIdCounter,
          x: spawnX,
          y: spawnY,
          initialX: spawnX, // Store initial position for extrapolation
          initialY: spawnY,
          vx: vx,
          vy: vy,
          angle: projectileAngle,
          projectileLifetime: weapon.projectileLifetime,
          createdAt: now,
          predicted: true,
          ownerId: localId,
          ownerTeam: players.get(localId)?.team ?? null,
          terminationTime: terminationTime, // Time in ms after creation when projectile stops
          // Sprite information from weapon
          sprite: weapon.projectileSprite,
          spriteWidth: weapon.projectileWidth,
          spriteHeight: weapon.projectileHeight,
          rotates: weapon.projectileRotates,
          tracerColor: weapon.tracerColor,
          projectileSize: weapon.projectileSize
      });
    }

    // Play weapon fire sound effect (use the weapon from the player)
    const fireSound = getWeaponSound(weapon, 'fire');
    if (fireSound) {
        fireSound.currentTime = 0; // Reset to start in case it's already playing
        fireSound.play().catch(() => {
            // Silently ignore if browser blocks autoplay
        });
    }
}

function isPickupWithinRange(player, radius = PICKUP_INTERACT_RADIUS) {
  if (!player) return false;
  const objects = worldObjects;
  if (!objects) return false;
  const radiusSq = radius * radius;
  const iterable = objects instanceof Map ? objects.values() : objects;
  for (const obj of iterable) {
    if (!obj) continue;
    const type = typeof obj.type === 'string' ? obj.type : '';
    if (!type.includes('pickup')) continue;
    if (typeof obj.x !== 'number' || typeof obj.y !== 'number') continue;
    const dx = obj.x - player.x;
    const dy = obj.y - player.y;
    if ((dx * dx + dy * dy) <= radiusSq) {
      return true;
    }
  }
  return false;
}

function queueGamepadReload() {
  const reloadCheck = canLocalPlayerReload();
  if (reloadCheck !== true) {
    return;
  }
  pendingGamepadReload = true;
}

function consumePendingGamepadReload() {
  if (!pendingGamepadReload) {
    return false;
  }
  pendingGamepadReload = false;
  return true;
}

function resetLocalContainerInteraction() {
  const interaction = ensureLootInteractionState();
  interaction.activeContainerId = null;
  interaction.progress = 0;
  interaction.holdDurationMs = 0;
  interaction.isHolding = false;
  interaction.localStartTime = 0;
  interaction.lastServerProgressMs = 0;
}

function cancelActiveContainerInteraction(reason = 'cancel') {
  const interaction = ensureLootInteractionState();
  if (interaction.activeContainerId) {
    socketManager.send('lootContainerCancelInteract', {
      containerId: interaction.activeContainerId,
      reason
    });
  }
  resetLocalContainerInteraction();
}

function isContainerInteractable(container) {
  if (!container) return false;
  if (container.interactionMode === 'damage') return false;
  if (container.state !== 'idle' && container.state !== 'interacting') return false;
  if (container.activeInteractor && container.activeInteractor !== localId) return false;
  return true;
}

function updateNearestLootContainer(player) {
  if (!player) {
    gameState.nearestLootContainerId = null;
    gameState.nearestLootContainerDistance = null;
    ensureLootInteractionState();
    cancelActiveContainerInteraction('player_unavailable');
    return;
  }

  let nearestId = null;
  let nearestDistance = Infinity;
  for (const [id, container] of lootContainers.entries()) {
    if (!isContainerInteractable(container)) continue;
    const baseRadius = typeof container.sensorRadius === 'number'
      ? container.sensorRadius
      : PICKUP_INTERACT_RADIUS;
    const interactionDims = container?.interactionDimensions || container?.dimensions || {};
    const width = interactionDims.width || container?.width || 0;
    const height = interactionDims.height || container?.height || 0;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const boundingRadius = Math.sqrt((halfWidth * halfWidth) + (halfHeight * halfHeight));
    const radius = Math.max(baseRadius, boundingRadius + 20);
    const dx = container.x - player.x;
    const dy = container.y - player.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= radius && distance < nearestDistance) {
      nearestDistance = distance;
      nearestId = id;
    }
  }

  gameState.nearestLootContainerId = nearestId;
  gameState.nearestLootContainerDistance = Number.isFinite(nearestDistance) ? nearestDistance : null;

  if (!nearestId && ensureLootInteractionState().activeContainerId) {
    cancelActiveContainerInteraction('out_of_range');
  }
}

function tryStartLootContainerInteraction() {
  if (!isInGame || isLocalPlayerDead()) return false;
  const containerId = gameState.nearestLootContainerId;
  if (!containerId) {
    lootLogger.debug('[LootContainer][Client] No nearby container to interact with');
    return false;
  }
  const container = lootContainers.get(containerId);
  if (!isContainerInteractable(container)) {
    lootLogger.debug('[LootContainer][Client] Container not interactable:', containerId, container);
    return false;
  }

  const interaction = ensureLootInteractionState();
  if (interaction.activeContainerId === containerId && interaction.isHolding) {
    lootLogger.debug('[LootContainer][Client] Already interacting with container:', containerId);
    return true;
  }

  lootLogger.info('[LootContainer][Client] Starting interaction with container:', {
    containerId,
    holdDuration: container?.holdDurationMs,
    state: container?.state,
    interactionMode: container?.interactionMode
  });
  socketManager.send('lootContainerInteract', { containerId });
  interaction.activeContainerId = containerId;
  interaction.progress = 0;
  interaction.isHolding = true;
  interaction.holdDurationMs = container?.holdDurationMs || interaction.holdDurationMs || 0;
  interaction.localStartTime = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();
  interaction.lastServerProgressMs = 0;
  return true;
}

function updateLootContainerInteractionProgress() {
  const interaction = ensureLootInteractionState();
  if (!interaction.isHolding) return;
  if (!interaction.holdDurationMs) return;
  const now = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();
  if (interaction.lastServerProgressMs && (now - interaction.lastServerProgressMs) < 120) {
    return;
  }
  const startTime = interaction.localStartTime || now;
  const elapsed = Math.max(0, now - startTime);
  if (!Number.isFinite(elapsed) || elapsed <= 0) return;
  const predicted = Math.min(1, elapsed / interaction.holdDurationMs);
  if (!Number.isFinite(predicted)) return;
  interaction.progress = Math.max(interaction.progress || 0, predicted);
}

/**
 * Whether the player's active inventory slot holds a consumable, in which case
 * the fire action uses it instead of shooting.
 * @param {object} player
 * @returns {boolean}
 */
function activeSlotIsConsumable(player) {
  const item = player?.inventory?.[player.activeSlotIndex]?.item;
  return Boolean(item && consumableRegistry.has(item.id));
}

/**
 * Handle mouse shooting (continuous fire for FULL_AUTO weapons)
 * Called every frame to support full-auto fire mode
 */
function handleMouseShooting() {
  if (!isInGame) return;

  const player = players.get(localId);
  if (!player || player.isDead) return;

  // Holding a consumable: the fire action is a single use (onShoot), never a
  // held-down repeat.
  if (activeSlotIsConsumable(player)) return;

  // Only handle full-auto weapons here
  // SEMI_AUTO and BURST weapons are handled by mousedown event only
  const weapon = player.equippedWeapon;
  if (!weapon || weapon.fireMode !== WeaponFireMode.FULL_AUTO) return; // normalized enum ('full_auto'), not the raw config spelling

  // Only fire if mouse button is held down
  if (!inputState.mouse.down) return;

  const shootCheck = typeof gameState.canLocalPlayerShoot === 'function'
    ? gameState.canLocalPlayerShoot()
    : true;
  if (shootCheck !== true) {
    inputState.mouse.down = false;
    return;
  }

  // Check if we can fire based on client-side state (respects fire rate)
  const weaponState = player.weaponState;

  // Apply ability multipliers to fire rate (e.g., SuperSpeed)
  const fireRateMultiplier = player.activeAbilityEffects?.fireRateMultiplier || 1.0;
  const stacks = player.inventory[player.activeSlotIndex]?.stack || 1;
  const effectiveFireRate = (weapon.getFireRateAtStack?.(stacks) ?? weapon.fireRate) / fireRateMultiplier;

  // Client-side fire rate check (same as in predictAndSpawnLocalBullet)
  if (weaponState.reloading || weaponState.currentAmmo <= 0) return;
  const seq = shotSeqFor(player.inputSeq);
  if (!passesFireRate(player, seq, effectiveFireRate)) return;

  // Fire locally for prediction
  if (predictAndSpawnLocalBullet(seq) === 'rate') return;

  // Always emit to server when we fire
  // Server will do its own validation (ammo, fire rate, etc.)
  sendShoot(player.angle, seq);
}

/**
 * Fire. `viewTime` is the server-clock time of the world this client is looking at
 * (remote players are rendered interpolationDelay() behind serverNow()); the server
 * evaluates the shot against where everyone was at that moment (lag compensation).
 */
function sendShoot(angle, seq) {
  const payload = { angle, viewTime: gameState.serverNow() - gameState.interpolationDelay() };
  if (seq != null) payload.seq = seq;
  socketManager.send('shoot', payload);
}

function passesFireRate(player, seq, interval) {
  if (seq === null) return !(clientLastShotTime && performance.now() - clientLastShotTime < interval);
  return canFireAtSeq(player.lastShotSeq, seq, interval);
}

/**
 * Fire whatever is in the active inventory slot: use the consumable, swing the
 * pickaxe, or shoot the equipped gun. This is the single "use what I'm
 * holding" action — shared by the mouse-click path (InputManager's onShoot
 * callback) and the gamepad RT-trigger path (handleGamepadButtons) so both
 * input devices branch on consumable/melee/gun identically.
 */
function fireEquippedItem() {
  const player = players.get(localId);
  if (!player || player.isDead) return;

  // The fire action means "use what I am holding". Holding a consumable,
  // that is a timed use, not a shot.
  if (activeSlotIsConsumable(player)) {
    socketManager.send('useItem', {});
    return;
  }

  const seq = shotSeqFor(player.inputSeq);

  // The pickaxe swings instead of firing: same "use what I'm holding"
  // button, different server message, no projectile.
  if (player.equippedWeapon?.fireMode === WeaponFireMode.MELEE) {
    playerLastShotTime.set(localId, performance.now()); // reuses the gun idle/firing sprite timer
    const cooldown = player.equippedWeapon.swingCooldownMs || 500;
    if (seq !== null && !canFireAtSeq(player.lastSwingSeq, seq, cooldown)) return;
    if (seq !== null) player.lastSwingSeq = seq;
    const swing = { angle: player.angle, viewTime: gameState.serverNow() - gameState.interpolationDelay() };
    if (seq !== null) swing.seq = seq;
    socketManager.send('meleeSwing', swing);
    return;
  }

  if (predictAndSpawnLocalBullet(seq) === 'rate') return;
  sendShoot(player.angle, seq);
}

/**
 * Handle gamepad button inputs (shooting, abilities, reload)
 * Called every frame before input gathering
 */
function handleGamepadButtons() {
  if (!gamepadManager.connected || !isInGame) return;

  const player = players.get(localId);
  if (!player || player.isDead) return;

  // Shooting (RT Trigger). Full-auto guns keep firing every frame the
  // trigger is held (predictAndSpawnLocalBullet's own fire-rate gate spaces
  // out the actual shots) — unchanged from before. Everything else (melee,
  // consumables, semi-auto/burst guns) fires once per press, exactly like a
  // mouse click: this mirrors InputManager's own handleGamepadButtons, which
  // already gates on canShoot before invoking onShoot.
  const isFullAutoGun = player.equippedWeapon?.fireMode === WeaponFireMode.FULL_AUTO;
  const rtTriggered = isFullAutoGun
    ? gamepadManager.isTriggerPressed('RT')
    : gamepadManager.isButtonJustPressed('RT');

  if (rtTriggered) {
    const shootCheck = typeof gameState.canLocalPlayerShoot === 'function'
      ? gameState.canLocalPlayerShoot()
      : true;

    if (shootCheck === true) {
      // Same branching logic the mouse-click handler (onShoot) uses.
      fireEquippedItem();
    } else {
      inputState.mouse.down = false;
    }
  }

  // Inventory scrolling (LB = previous, RB = next)
  if (gamepadManager.isButtonJustPressed('LB')) {
    switchInventorySlot(-1);
  }
  if (gamepadManager.isButtonJustPressed('RB')) {
    switchInventorySlot(1);
  }

  // Pickup or reload (X button) - prioritize loot container interaction
  if (gamepadManager.isButtonJustPressed('X')) {
    const started = tryStartLootContainerInteraction();
    if (!started) {
      socketManager.send('manualPickup');
      if (!isPickupWithinRange(player)) {
        queueGamepadReload();
      }
    }
  }

  if (gamepadManager.isButtonJustReleased('X')) {
    cancelActiveContainerInteraction('gamepad_button_up');
  }

  // Ability - Movement (B button)
  if (gamepadManager.isButtonJustPressed('B')) {
    activateAbility('movement');
  }

  // Ability - Utility (Y button)
  if (gamepadManager.isButtonJustPressed('Y')) {
    activateAbility('utility');
  }
}

// ─────────────────────────────────────────────────────────────
// MAIN UPDATE - Runs Fixed DT
// ─────────────────────────────────────────────────────────────
function update(dt) {
    const player = players.get(localId);
    const lootInteraction = ensureLootInteractionState();
    updateLootContainerInteractionProgress();

    // Update elimination feed and notification regardless of player state
    updateEliminationFeed();
    updateEliminationNotification();
    updateDeathNotification();
    updateFloatingDamageNumbers();
    gameState.expireTransients(Date.now(), performance.now());

    // When not in game (match ended / splash / lobby), or dead player:
    // don't process inputs, movement prediction, or continuous sounds
    if (!isInGame || !gameState.isInGame || !player || player.isDead) {
        updateNearestLootContainer(null);
        if (lootInteraction.activeContainerId) {
            cancelActiveContainerInteraction('player_unavailable');
        }
        // Stop footsteps when not in game or player is dead
        playerSoundManager.stopFootsteps();

        // Still clean up predicted projectiles for visual consistency
        simulateProjectiles();
        return;
    }

    updateNearestLootContainer(player);

    // Update spatial audio listener position
    spatialAudio.updateListenerPosition(player.x, player.y);
    if (invisiblePresenceCue) {
        invisiblePresenceCue.update(player);
    }

    // Handle continuous mouse shooting (for full-auto weapons)
    handleMouseShooting();

    // Handle gamepad buttons (shooting, abilities, reload)
    handleGamepadButtons();

    const input = gatherCurrentInput();

    // Update angle instantly (used by prediction)
    player.angle = input.angle;

    // 1. Record input
    const seq = player.inputSeq++;
    player.inputHistory.push({ seq, input: { ...input }, sendTime: performance.now() });
    if (player.inputHistory.length > 100) player.inputHistory.shift();

    // 2. Predict movement (every fixed tick)
    player.predict(dt, input);

    // Sounds after predict so a drift that starts this tick plays immediately
    playerSoundManager.update(player);

    // 3. Send input packet
    sendInputPacket(seq, input);

    // 4. Clean up predicted projectiles
    simulateProjectiles();
}

/** One projectile-sim step (prune predicted, publish gameState.visibleProjectiles). */
function simulateProjectiles() {
  stepProjectiles(gameState, performance.now(), { mapWidth, mapHeight, interpolationDelay: gameState.interpolationDelay() });
}

// ─────────────────────────────────────────────────────────────
// Game Loop (using GameLoop class)
// ─────────────────────────────────────────────────────────────
const gameLoop = new GameLoop(
  // Update callback - called at fixed timestep
  (dt) => update(dt),
  // Render callback - called every frame
  (currentTime, alpha) => render(currentTime, alpha),
  // Options
  { fixedDt: GameState.FIXED_DT, maxCatchupFrames: 4 }
);

// Set gamepad polling as pre-frame callback
gameLoop.setPreFrameCallback(() => gamepadManager.poll());

// ─────────────────────────────────────────────────────────────
// SPLASH SCREEN AND LOBBY
// ─────────────────────────────────────────────────────────────
// UI is now handled by UIManager (ui/UIManager.js)
// These functions delegate to UIManager for backward compatibility

/**
 * Initialize splash screen - delegates to UIManager
 * Called during game initialization
 */
function initializeSplashScreen() {
  UIManager.init({
    gameState: gameState,
    callbacks: {
      onReady: handlePlayerReady,
      onUnready: handlePlayerUnready,
      onReturnToLobby: returnToLobby,
      onLocker: () => UIManager.openLocker(),
      onSpectate: startSpectating,
      onSubmitFlagNote: patchFlagNote
    },
    locker: {
      loadout,
      store: loadoutStore,
      cosmeticsConfig: gameState.cosmeticsConfig,
      getSkinImage,
      getBackpackImage,
      createPreview: (host) => createLockerPreview(host)
    },
    // Stats phase: the account and leaderboard screens fetch through this seam.
    fetchFn: (...args) => fetch(...args),
    user: Auth.isLoggedIn() ? Auth.getUser() : null
  });

  // Debug panel "Start Game" (the server only offers it outside production).
  DebugStartGameButton.init({ onForceStart: () => socketManager.send('forceStart') });
  DebugStartGameButton.setDebugPanelVisible(debugPanelVisible);

  // auth.js resolves /auth/me asynchronously on DOM ready, so the nav above may
  // have been built before the session was known. Re-seed it once it settles —
  // this is what makes the Account item appear for a signed-in player.
  Auth.checkAuthStatus()
    .then((data) => UIManager.setAuthUser(data?.authenticated ? data.user : null))
    .catch(() => { /* guest session; the nav is already correct */ });
}

/**
 * Handle player ready action (callback from UIManager)
 * @param {string} trimmedName - Player name
 */
function handlePlayerReady(trimmedName) {
  gameState.playerName = trimmedName;
  playerName = trimmedName;
  const selectedModeId = UIManager.getSelectedGameModeId();
  networkLogger.debug('[handlePlayerReady] selectedModeId:', selectedModeId);

  // Check if already in a room (after unreadying)
  if (gameState.currentRoomId) {
    // Already in room, just mark as ready again
    UIManager.setReadyButtonState('readying');
    networkLogger.info('[handlePlayerReady] Re-readying in existing room, emitting ready with gameModeId:', selectedModeId);
    socketManager.send('ready', { gameModeId: selectedModeId });
  } else {
    // First time joining, connect and join room
    UIManager.setReadyButtonState('joining');

    // Define handler to send player name once connected
    const onConnected = () => {
      networkLogger.debug('[handlePlayerReady] onConnected fired, socket.connected:', socket.connected, 'socket.id:', socket.id);
      
      // Start ping tracking for latency measurement
      socketManager.startPingTracking((ping) => {
        currentPing = ping;
        gameState.netStats.onPing(ping);
        gameState.netStats.setTransport(socketManager.transportName?.() ?? null);
      });

      const payload = {
        name: playerName,
        sessionToken: socketManager.sessionToken,
        protocolVersion: Protocol.PROTOCOL_VERSION,
        ...loadout.toJoinPayload()
      };
      if (selectedModeId) {
        payload.gameModeId = selectedModeId;
      }
      // Kept so a mid-match reconnect can re-send the exact same join request
      gameState.lastJoinPayload = payload;
      networkLogger.info('[handlePlayerReady] Emitting setPlayerName with payload:', payload);
      socketManager.send('setPlayerName', payload);
    };

    networkLogger.debug('[handlePlayerReady] socket.connected before connect:', socket.connected, 'socket.id:', socket.id);
    
    // If already connected, send immediately; otherwise wait for connect event
    if (socket.connected) {
      networkLogger.debug('[handlePlayerReady] Already connected, calling onConnected immediately');
      onConnected();
    } else {
      networkLogger.debug('[handlePlayerReady] Not connected, registering once(connect) and calling socket.connect()');
      // Register the once listener BEFORE calling connect
      socket.once('connect', () => {
        networkLogger.debug('[handlePlayerReady] socket.once(connect) callback fired!');
        onConnected();
      });
      socket.connect();
      networkLogger.debug('[handlePlayerReady] socket.connect() called, socket.connected now:', socket.connected);
    }
  }
}

/**
 * Handle player unready action (callback from UIManager)
 */
function handlePlayerUnready() {
  socketManager.send('unready');
}

/**
 * Hide splash screen (game starting)
 */
function hideSplashScreen() {
  UIManager.hideSplashScreen();
  gameState.isInGame = true;
  isInGame = true; // Keep local accessor in sync

  // Enable Tab key override now that we're in game
  window._gameTabOverride = true;
}

/**
 * Update lobby UI with room data
 * @param {Object} roomData - Room state data
 */
function updateLobbyUI(roomData) {
  UIManager.updateLobbyUI(roomData);
  DebugStartGameButton.setRoomStatus(roomData);
}

/**
 * Show countdown message
 * @param {number} seconds - Countdown starting value
 */
function showCountdownMessage(seconds) {
  UIManager.showCountdownMessage(seconds);
}

/**
 * Update countdown display (server-driven ticks)
 * @param {number} seconds - Current countdown value from server
 */
function updateCountdown(seconds) {
  UIManager.updateCountdown(seconds);
}

/**
 * Hide countdown message
 */
function hideCountdownMessage() {
  UIManager.hideCountdownMessage();
}

/**
 * Show game end screen
 * @param {Object} data - Game end data
 */
async function showGameEndScreen(data) {
  // Clear any stuck keys or input states
  clearAllKeys();
  if (typeof InputManager !== 'undefined' && InputManager.clearAllInput) {
    InputManager.clearAllInput();
  }

  // Zero velocity on local player so residual motion does not trigger movement
  const localPlayer = players.get(localId);
  if (localPlayer) {
    localPlayer.vx = 0;
    localPlayer.vy = 0;
  }

  // Stop footsteps for local player in all end screen cases
  playerSoundManager.stopFootsteps();

  const isMatchEnd = !data?.phase || data.phase === 'matchEnd';

  // Stop all player and spatial sounds only when the match is truly over,
  // preserving death and nearby combat sounds for mid-match spectators.
  if (isMatchEnd) {
    playerSoundManager.stopAll();
    if (spatialAudio && typeof spatialAudio.stopAll === 'function') {
      spatialAudio.stopAll();
    }
  }

  // A fresh recap (early elimination or the real match end) always supersedes
  // whatever spectate target was set from a previous elimination.
  gameState.spectateTargetId = null;

  const pendingFlags = await pendingFlagsForRecap();
  const payload = { ...data, pendingFlags };

  if (data.victory && data.phase === 'matchEnd') {
    UIManager.showVictoryScreen(payload);
  } else {
    UIManager.showGameEndScreen(payload);
  }
}

/**
 * Watch the rest of the match (callback from GameEndScreen's Spectate button).
 * The server picks who we follow — real players before bots — and tells us
 * via 'spectateTarget'; our eliminator is only a hint.
 * @param {Object} data - The recap data the Spectate button was shown on
 */
function startSpectating(data) {
  socketManager.send('spectate', data.killedBy ? { targetId: data.killedBy } : {});
}

/**
 * Return to lobby after game ends
 */
function returnToLobby() {
  // Hide game end screen via UIManager
  UIManager.hideGameEndScreen();

  teardownToLobby();

  // Same as Unready: this disconnect is intentional. Without the suppress,
  // the next Ready click's connect is classified as a recovery reconnect and
  // torn down with "Reconnected to server. Please ready up again."
  socketManager.suppressReconnectNotificationOnce();
  socket.disconnect();
}

// ─────────────────────────────────────────────────────────────
// CONSOLE HELPER FUNCTIONS (for testing/debugging)
// ─────────────────────────────────────────────────────────────

/**
 * Equip an ability to a specific slot (TESTING HELPER)
 * Usage from browser console:
 *   equipAbility('utility', 'invisibility')
 *   equipAbility('movement', 'teleport')
 *
 * @param {string} category - 'utility' or 'movement'
 * @param {string} abilityId - ability ID (e.g., 'invisibility', 'teleport', 'invulnerability')
 * @param {number} stacks - number of stacks (default: 1)
 */
window.equipAbility = function(category, abilityId, stacks = 1) {
  if (!isInGame || !localId) {
    devLogger.warn('❌ You must be in-game to equip abilities');
    return;
  }

  if (!['utility', 'movement'].includes(category)) {
    devLogger.warn(`❌ Invalid category: "${category}". Use "utility" or "movement"`);
    return;
  }

  // Check if ability exists (if abilityRegistry is available)
  if (typeof abilityRegistry !== 'undefined' && !abilityRegistry.get(abilityId)) {
    devLogger.warn(`❌ Unknown ability: "${abilityId}". Available abilities:`, abilityRegistry.getAllIds());
    return;
  }

  devLogger.info(`⚡ Equipping ${abilityId} (stack ${stacks}) to ${category} slot`);

  // Send request to server. Wait for authoritative confirmation
  // (server will emit 'abilityEquipSuccess' or 'abilityEquipFailed')
  try {
    socketManager.send('equipAbility', { category, abilityId, stacks });
    devLogger.debug('→ Sent equip request to server, awaiting confirmation...');
  } catch (err) {
    devLogger.warn('Failed to send equipAbility to server', err);
  }

  devLogger.info(`✅ ${abilityId} equipped! Press ${category === 'utility' ? 'Q' : 'E'} to activate`);
};

/**
 * Equip a weapon by ID
 * Usage from browser console:
 *   equipWeapon('assault_rifle')
 *   equipWeapon('laser_rifle')
 *
 * @param {string} weaponId - weapon ID (e.g., 'assault_rifle', 'laser_rifle', 'plasma_pistol')
 */
window.equipWeapon = function(weaponId) {
  if (!isInGame || !localId) {
    devLogger.warn('❌ You must be in-game to equip weapons');
    return;
  }

  // Check if weapon exists (if weaponRegistry is available)
  if (typeof weaponRegistry !== 'undefined') {
    const weapon = weaponRegistry.get(weaponId);
    if (!weapon) {
      devLogger.warn(`❌ Unknown weapon: "${weaponId}". Available weapons:`);
      window.listWeapons();
      return;
    }
    devLogger.info(`🔫 Equipping ${weapon.name}...`);
  } else {
    devLogger.info(`🔫 Equipping ${weaponId}...`);
  }

  // Send request to server
  try {
    socketManager.send('equipWeapon', { weaponId });
    devLogger.debug('→ Sent equip request to server, awaiting confirmation...');
  } catch (err) {
    devLogger.warn('Failed to send equipWeapon to server', err);
  }
};

/**
 * List all available weapons
 * Usage from browser console: listWeapons()
 */
window.listWeapons = function() {
  if (typeof weaponRegistry === 'undefined') {
    devLogger.warn('❌ Weapon registry not loaded yet');
    return;
  }

  const weapons = weaponRegistry.getAll();
  devLogger.info('🔫 Available weapons:');
  weapons.forEach(weapon => {
    devLogger.info(`  - ${weapon.id}: ${weapon.name} (${weapon.rarity || 'COMMON'})`);
  });

  devLogger.info('\n💡 Usage: equipWeapon("assault_rifle")');
};

/**
 * List all available abilities
 * Usage from browser console: listAbilities()
 */
window.listAbilities = function() {
  if (typeof abilityRegistry === 'undefined') {
    devLogger.warn('❌ Ability registry not loaded yet');
    return;
  }

  const abilities = abilityRegistry.getAll();
  devLogger.info('⚡ Available abilities:');
  abilities.forEach(ability => {
    devLogger.info(`  - ${ability.id} (${ability.category}): ${ability.description}`);
  });

  devLogger.info('\n💡 Usage: equipAbility("utility", "invisibility") or equipAbility("movement", "teleport")');
};

/**
 * Show current equipped abilities
 * Usage from browser console: showEquipped()
 */
window.showEquipped = function() {
  devLogger.info('⚡ Currently equipped abilities:');
  devLogger.info('  Utility (Q):', abilityState.equipped.utility);
  devLogger.info('  Movement (E):', abilityState.equipped.movement);
};

/**
 * Debug: Check if player can shoot and why
 * Usage from browser console: debugCanShoot()
 */
window.debugCanShoot = function() {
  const localPlayer = players.get(localId);
  devLogger.info('🔍 DEBUG: Can Shoot Status');
  devLogger.info('─────────────────────────────');
  devLogger.info('Local player exists:', !!localPlayer);
  devLogger.info('Local player ID:', localId);
  devLogger.info('activeAbilityEffects:', localPlayer?.activeAbilityEffects);
  devLogger.info('abilitiesConfig loaded:', !!gameState.abilitiesConfig);

  if (localPlayer?.activeAbilityEffects && gameState.abilitiesConfig?.abilities) {
    devLogger.info('\n🔍 Active Abilities:');
    for (const abilityId in localPlayer.activeAbilityEffects) {
      const ability = gameState.abilitiesConfig.abilities.find(a => a.id === abilityId);
      devLogger.info(`  - ${abilityId}:`, ability ? `canShoot = ${ability.canShoot}` : 'NOT FOUND IN CONFIG');
    }
  }

  const result = canLocalPlayerShoot();
  devLogger.info('\n📊 Result:', result === true ? '✅ CAN SHOOT' : `❌ BLOCKED BY: ${result?.name || 'Unknown'}`);
};

/**
 * Debug: Toggle muzzle offset visualization
 * Usage from browser console:
 *   window.debugMuzzleOffset = true   // Enable
 *   window.debugMuzzleOffset = false  // Disable
 */
Object.defineProperty(window, 'debugMuzzleOffset', {
  get: () => debugMuzzleOffset,
  set: (value) => {
    debugMuzzleOffset = !!value;
    devLogger.info(`🎯 Muzzle offset debug visualization ${debugMuzzleOffset ? 'ENABLED' : 'DISABLED'}`);
  }
});

// ─────────────────────────────────────────────────────────────
// NETWORK EVENT REGISTRATION
// ─────────────────────────────────────────────────────────────
function registerNetworkEvents() {
  // Register Room Events
  socketManager.registerEventModule(RoomEvents, {
    reloadMap: (mapPath) => reloadMap(mapPath),
    updateLobbyUI,
    showCountdownMessage,
    updateCountdown,
    hideCountdownMessage,
    suppressReconnectNotification: () => socketManager.suppressReconnectNotificationOnce(),
    setLobbyInputsLocked: (locked) => UIManager.setLobbyInputsLocked(locked),
    teardownToLobby,
    stopWarmupSounds: () => {
      playerSoundManager.stopAll();
      if (spatialAudio && typeof spatialAudio.stopAll === 'function') {
        spatialAudio.stopAll();
      }
      for (const audio of weaponSoundCache.values()) {
        try {
          if (audio && typeof audio.pause === 'function') {
            audio.pause();
            audio.currentTime = 0;
          }
        } catch (err) {
          // Already stopped
        }
      }
      for (const audio of [deathSoundAudio, eliminationSoundAudio]) {
        if (!audio || typeof audio.pause !== 'function') continue;
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch (err) {
          // Already stopped
        }
      }
    }
  });

  // Register Game Events
  socketManager.registerEventModule(GameEvents, {
    hideSplashScreen,
    getDefaultWeapon,
    createLocalPlayer: (config) => new LocalPlayer(config),
    createRemotePlayer: (config) => new RemotePlayer(config),
    loadWorldObjectImage: (obj) => preloadWorldObjectVisual(obj),
    playOtherPlayerShootSound,
    // Initialize player sound manager with player config
    initializePlayerSounds: (playerConfig) => {
      if (playerConfig) {
        playerSoundManager.initialize(playerConfig, spatialAudio);
      }
    },
    // Used by the rejoinFailed handler when the match can't be resumed
    resetToLobbyAfterReconnect,
    // Sync local accessor variables with gameState after spawn
    syncLocalAccessors: () => {
      localId = gameState.localId;
      mapConfig = gameState.mapConfig;
      mapWidth = gameState.mapWidth;
      mapHeight = gameState.mapHeight;
      currentRoomId = gameState.currentRoomId;
    }
  });

  // Register Melee Events (other clients seeing a swing, played via the same
  // playerLastShotTime timer GameEvents' projectileSpawned uses for guns)
  socketManager.registerEventModule(MeleeEvents, {});

  // Create floating number helper (used by both Combat and Ability events)
  const createFloatingNumber = (playerId, damage, x, y, impact = {}) => {
    const now = performance.now();
    const lastSign = damageNumberToggle.get(playerId) || 1;
    const sign = -lastSign;
    damageNumberToggle.set(playerId, sign);
    const speed = DAMAGE_NUMBER_HORZ_MIN + Math.random() * (DAMAGE_NUMBER_HORZ_MAX - DAMAGE_NUMBER_HORZ_MIN);
    const angle = (Math.random() * 10 - 5) * (Math.PI / 180);
    const vAng = (Math.random() * 0.6 - 0.3) * (Math.PI / 180);

    const shieldDamage = Math.max(0, Math.min(damage, impact.shieldDamage || 0));
    const healthDamage = Math.max(0, impact.healthDamage ?? (damage - shieldDamage));
    const shieldHit = shieldDamage > 0;

    floatingDamageNumbers.push({
      playerId,
      damage,
      shieldDamage,
      healthDamage,
      shieldHit,
      type: impact.type || 'damage',
      createdAt: now,
      x,
      y: y - 60,
      vx: sign * speed,
      angle,
      vAng
    });
  };

  // Register Combat Events
  socketManager.registerEventModule(CombatEvents, {
    spatialAudio,
    showEliminationNotification,
    addEliminationToFeed,
    showGameEndScreen,
    createFloatingDamageNumber: createFloatingNumber,
    syncIsInGame: () => { isInGame = gameState.isInGame; },
    showDeathNotification,
    playDeathSound,
    playEliminationSound
  });

  // Register Ability Events
  socketManager.registerEventModule(AbilityEvents, {
    spatialAudio,
    showNotification,
    showEliminationNotification,
    getTeleportTrailConfig,
    createFloatingDamageNumber: createFloatingNumber
  });

  // Register Weapon Events
  socketManager.registerEventModule(WeaponEvents, {
    getWeaponSound
  });

  socketManager.registerEventModule(CosmeticEvents, {
    notifySuccess: (message) => NotificationSystem.success(message),
    onUnlocked: (items) => loadoutStore?.grant(items)
  });

  socketManager.registerEventModule(RankedEvents, {
    notifySuccess: (message) => NotificationSystem.success(message),
    notifyError: (message) => NotificationSystem.error(message),
    applyRankedResult: (data) => UIManager.applyRankedResult(data)
  });

  socketManager.registerEventModule(ProgressionEvents, {
    notifySuccess: (message) => NotificationSystem.success(message)
  });

  socketManager.registerEventModule(FeedbackEvents, {
    flagStore,
    notifyError: (message) => NotificationSystem.error(message),
    onComposeAck: (data) => { if (data?.id) openComposer(data.id); }
  });

  // Register Pickup Events
  socketManager.registerEventModule(PickupEvents, {
    showNotification,
    playSound: null, // TODO: Add sound system integration
    loadWorldObjectImage: (obj) => preloadWorldObjectVisual(obj)
  });

  socketManager.registerEventModule(ItemEvents, {
    playItemSound: (soundPath, playerId, volume = 1) => {
      const player = players.get(playerId);
      if (!soundPath || !player) return;
      spatialAudio.playAt(soundPath, player.x, player.y, {
        config: SPATIAL_AUDIO_CONFIG.effects,
        volume
      });
    }
  });

  socketManager.registerEventModule(LootContainerEvents, {
    showNotification,
    loadWorldObjectImage: (obj) => preloadWorldObjectVisual(obj),
    playSpatialSound: (soundPath, x, y, volume = 1) => {
      if (!soundPath) return;
      spatialAudio.playAt(soundPath, x, y, {
        config: SPATIAL_AUDIO_CONFIG.effects,
        volume
      });
    },
    resetLocalInteraction: resetLocalContainerInteraction
  });

  // Listen for inventory slot swap confirmation
  socket.on('inventorySlotsSwapped', (data) => {
    console.log('[Client] Inventory slots swapped received:', data);
    const localPlayer = players.get(localId);
    console.log('[Client] Local player:', localPlayer);
    console.log('[Client] Current inventory:', localPlayer?.inventory);
    
    if (localPlayer && data.inventory) {
      // Update local player inventory to match server
      localPlayer.inventory = data.inventory;
      console.log('[Client] Updated inventory:', localPlayer.inventory);
      
      // The inventory will be updated in the next render cycle when updateInventorySlots is called
      // No need to force update - the normal game loop handles it
    }
  });

  // Listen for inventory item drop confirmation
  socket.on('inventoryItemDropped', (data) => {
    console.log('[Client] Inventory item dropped received:', data);
    const localPlayer = players.get(localId);
    
    if (localPlayer && data.inventory) {
      // Update local player inventory to match server
      localPlayer.inventory = data.inventory;
      console.log('[Client] Item dropped from slot', data.slotIndex, ', updated inventory:', localPlayer.inventory);
    }
  });
}

/**
 * Load map data from a Tiled TMJ file
 * @param {string} mapUrl - URL to the map file
 */
async function loadMapData(mapUrl) {
  // Asset paths in TMJ are relative (e.g., 'assets/art/world/...'), 
  // prepend '/' to load from public folder root
  const tiledData = await TiledMapLoader.load(mapUrl, '/');

  // Update game state with map dimensions
  if (tiledData) {
    gameState.mapWidth = tiledData.width;
    gameState.mapHeight = tiledData.height;
    clientLogger.info('[Client] Map dimensions updated', { 
      width: gameState.mapWidth, 
      height: gameState.mapHeight 
    });
  }

  // Pass Tiled data to renderer
  if (pixiRendererInitialized && PixiRenderer.setTiledData) {
    PixiRenderer.setTiledData(tiledData);
  }

  // Redraw walls with new dimensions
  if (pixiRendererInitialized && PixiRenderer.redrawWalls) {
    PixiRenderer.redrawWalls(gameState.mapWidth, gameState.mapHeight);
  }

  return tiledData;
}

// Initialize configs and assets
(async function initialize() {
  // Load configs first
  await loadConfigs();
  initializeInvisiblePresenceCue();

  // Load lobby game modes so the selector can render
  await loadLobbyGameModes();

  // Load weapons from server (needed for sprite loading)
  await configRegistry.loadWeapons();

  // Update currentWeapon now that weapons are loaded
  currentWeapon = getDefaultWeapon();

  // Then load skins based on cosmetics config
  loadAllSkins();
  loadAllBackpacks();

  // Load weapon and projectile sprites (after weapons are loaded)
  preloadWeaponSprites();

  // Wait for all skins to load
  await Promise.all(skinLoadPromises);

  // Lobby loadout: pure state + store. Ownership comes from the server; when the
  // request fails only defaults count as owned (fail closed, spec 6A D5).
  loadout = new Loadout(gameState.cosmeticsConfig);
  loadoutStore = createLoadoutStore({
    loadout,
    fetchFn: (url, opts) => fetch(url, opts),
    storage: (() => { try { return window.localStorage; } catch { return null; } })(),
    onError: (e) => clientLogger.warn('[Loadout] ' + (e?.message || e)),
    // A failed save must be visible (spec 6A D4): the local choice still applies this
    // session. Only equips reach this, and UIManager.init() has run by then.
    onPersistError: () => showNotification("Couldn't save your locker — it still applies this session", 'warning', 5000)
  });
  await loadoutStore.load();

  // Register network event handlers
  registerNetworkEvents();

  // Initialize PixiJS WebGL renderer
  try {
    const pixiContainer = document.body;
    pixiRendererInitialized = await PixiRenderer.init(pixiContainer, {
      send: (event, payload) => socketManager.send(event, payload)
    });
  } catch (e) {
    clientLogger.error('PixiJS initialization failed:', e);
    pixiRendererInitialized = false;
    alert('Failed to initialize WebGL renderer. Please ensure your browser supports WebGL.');
    return;
  }

  // Map will be loaded dynamically when joining a room
  // Don't load a default map - wait for server to specify which map to use
  clientLogger.info('Waiting for room to specify map...');

  // Map reload used by RoomEvents (injected as a callback, not a window global)
  reloadMap = async (mapPath) => {
    try {
      clientLogger.info('Reloading map', { mapPath });
      await loadMapData('/' + mapPath);
      clientLogger.info('Map reloaded successfully', { mapPath });
    } catch (e) {
      clientLogger.error('Failed to reload map', { mapPath, error: e });
    }
  };

  // Initialize unified input system (keyboard, mouse, gamepad)
  InputManager.init({
    canvas: PixiRenderer.getCanvas(),
    gameState: gameState,
    callbacks: {
      onShoot: () => {
        // Handle shooting for all fire modes on initial click
        // handleMouseShooting() handles continuous fire for FULL_AUTO
        fireEquippedItem();
      },
      onReload: () => {
        // Reload is not its own message: it rides on the next input packet's
        // `reload` flag, which gatherCurrentInput() reads from this flag.
        const player = players.get(localId);
        if (player && !player.isDead) queueGamepadReload();
      },
      onAbility: activateAbility,
      onInventorySwitch: (direction) => {
        // Routes through the same helper gamepad LB/RB uses (Player.switchSlot,
        // which skips empty slots), so every input method agrees on where
        // "next slot" lands.
        switchInventorySlot(direction);
      },
      onPickup: () => {
        socketManager.send('manualPickup');
      },
      onQuickSwapPickaxe: () => {
        const player = players.get(localId);
        if (!player || player.activeSlotIndex === 0) return;
        socketManager.send('switchInventorySlot', { slotIndex: 0 });
      },
      onSwitchToSlot: (slotIndex) => {
        const player = players.get(localId);
        if (!player || player.activeSlotIndex === slotIndex) return;
        socketManager.send('switchInventorySlot', { slotIndex });
      },
      showNotification: (msg, type) => {
        if (typeof showNotification === 'function') showNotification(msg, type);
      },
      canShoot: canLocalPlayerShoot,
      canReload: canLocalPlayerReload,
      onFlag: () => {
        if (!sendFlag()) return;
        NotificationSystem.info('Flagged — F9 to add a note', 1500);
      },
      onFlagCompose: () => {
        flagStore.awaitCompose = true;
        if (!sendFlag()) {
          flagStore.awaitCompose = false;
        }
      }
    }
  });
  // Get gamepad reference from InputManager (used by gatherCurrentInput)
  gamepadManager = InputManager.getGamepad();

  // Initialize splash screen
  initializeSplashScreen();

  // Locker button chip (equipped-skin thumbnail) is disabled for now; see setLockerChip.

  // Apply lobby mode list (after UIManager initialized)
  UIManager.setGameModes(lobbyGameModes, lobbyDefaultModeId);

  // Start game loop (will render grid even in pre-game state)
  gameLoop.start();
})();

export { clientLoggerFactory, nativeConsole, fallbackLogger, createClientLogger, clientLogger, networkLogger, lootLogger, audioLogger, devLogger, gameState, pixiRendererInitialized, socketManager, socket, playerSoundManager, spatialAudio, invisiblePresenceCue, resetToLobbyAfterReconnect, DEBUG_MODE, INTERPOLATION_DELAY, RECONCILE_THRESHOLD, RECONCILE_SMOOTH_FACTOR, DAMAGE_NUMBER_DURATION, DAMAGE_NUMBER_FLOAT_SPEED, DAMAGE_NUMBER_FADE_START, DAMAGE_NUMBER_HORZ_MIN, DAMAGE_NUMBER_HORZ_MAX, players, keys, inputState, projectiles, predictedProjectiles, serverProjectileState, predictedProjectileIdCounter, mapWidth, mapHeight, mapConfig, localId, isInGame, playerName, currentRoomId, roomState, lobbyGameModes, lobbyDefaultModeId, worldObjects, lootContainers, ensureLootInteractionState, eliminationFeed, eliminationNotification, deathNotification, floatingDamageNumbers, damageNumberToggle, abilityState, mouseWorldX, mouseWorldY, currentFps, frameCount, lastFpsUpdate, currentPing, lastPingTime, clientLastShotTime, pendingGamepadReload, loadConfigs, initializeInvisiblePresenceCue, loadLobbyGameModes, spriteCache, playerLastShotTime, debugMuzzleOffset, preloadWeaponSprites, gamepadManager, abilityIconCache, defaultAbilityIcon, weaponIconCache, PICKUP_INTERACT_RADIUS, deathSoundAudio, eliminationSoundAudio, worldObjectImageCache, NON_LOOPING_ANIMATION_STATES, preloadWorldObjectVisual, getWorldObjectRenderData, teleportTrails, isLocalPlayerDead, canLocalPlayerShoot, canLocalPlayerReload, showNotification, skinImages, skinLoadPromises, loadout, loadoutStore, loadSkin, loadAllSkins, getTeleportTrailConfig, getSkinImage, currentWeapon, weaponSoundCache, getWeaponSound, render, showEliminationNotification, updateEliminationNotification, showDeathNotification, updateDeathNotification, playDeathSound, playEliminationSound, addEliminationToFeed, updateEliminationFeed, updateFloatingDamageNumbers, lastAbilityActivation, ABILITY_ACTIVATION_DEBOUNCE, activateAbility, emitAimUpdate, gatherCurrentInput, switchInventorySlot, clearAllKeys, handleTabVisible, sendInputPacket, sendShoot, playOtherPlayerShootSound, predictAndSpawnLocalBullet, isPickupWithinRange, queueGamepadReload, consumePendingGamepadReload, resetLocalContainerInteraction, cancelActiveContainerInteraction, isContainerInteractable, updateNearestLootContainer, tryStartLootContainerInteraction, updateLootContainerInteractionProgress, handleMouseShooting, handleGamepadButtons, update, simulateProjectiles, gameLoop, initializeSplashScreen, handlePlayerReady, handlePlayerUnready, hideSplashScreen, updateLobbyUI, showCountdownMessage, updateCountdown, hideCountdownMessage, showGameEndScreen, returnToLobby, registerNetworkEvents, loadMapData };
